import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, AlertCircle, LogIn } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/common/SEOHead';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';

type InvitationIntent = 'collaborate' | 'review';
type PaperVisibility = 'public' | 'private';

type LookupState =
  | { kind: 'loading' }
  | { kind: 'invalid' }
  | { kind: 'expired' }
  | { kind: 'resolved'; status: string }
  | {
      kind: 'pending';
      paperId: string;
      paperTitle: string;
      authorName: string;
      inviteeEmail: string | null;
      expiresAt: string | null;
      intent: InvitationIntent;
      paperVisibility: PaperVisibility;
    };

const InvitationLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [state, setState] = useState<LookupState>({ kind: 'loading' });
  const [acting, setActing] = useState<'accept' | 'decline' | null>(null);

  useEffect(() => {
    let cancelled = false;
    const lookup = async () => {
      if (!token) {
        setState({ kind: 'invalid' });
        return;
      }
      const { data, error } = await supabase.functions.invoke('manage-review-invitation', {
        body: { action: 'lookup', token },
      });
      if (cancelled) return;
      if (error || !data) {
        setState({ kind: 'invalid' });
        return;
      }
      switch (data.state) {
        case 'pending':
          setState({
            kind: 'pending',
            paperId: data.paperId,
            paperTitle: data.paperTitle,
            authorName: data.authorName,
            inviteeEmail: data.inviteeEmail,
            expiresAt: data.expiresAt,
            intent: data.intent === 'collaborate' ? 'collaborate' : 'review',
            paperVisibility: data.paperVisibility === 'private' ? 'private' : 'public',
          });
          break;
        case 'expired':
          setState({ kind: 'expired' });
          break;
        case 'invalid':
          setState({ kind: 'invalid' });
          break;
        default:
          setState({ kind: 'resolved', status: data.state });
      }
    };
    lookup();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleDecline = async () => {
    if (!token) return;
    setActing('decline');
    const { error } = await supabase.functions.invoke('manage-review-invitation', {
      body: { action: 'decline', token },
    });
    setActing(null);
    if (error) {
      toast({ title: 'Could not record decline', description: error.message, variant: 'destructive' });
      return;
    }
    setState({ kind: 'resolved', status: 'declined' });
  };

  const handleAccept = async () => {
    if (!token) return;
    if (!user) {
      sessionStorage.setItem('redirectAfterAuth', `/invitations/${token}`);
      navigate(`/auth?return_to=${encodeURIComponent(`/invitations/${token}`)}`);
      return;
    }
    setActing('accept');
    const { data, error } = await supabase.functions.invoke('manage-review-invitation', {
      body: { action: 'accept', token },
    });
    setActing(null);
    if (error) {
      toast({ title: 'Could not accept', description: error.message, variant: 'destructive' });
      return;
    }
    toast({ title: 'Invitation accepted', description: 'You can now write your review.' });
    navigate(`/paper/${data?.paperId ?? ''}`);
  };

  const renderBody = () => {
    if (state.kind === 'loading') {
      return (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (state.kind === 'invalid') {
      return (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            This invitation link is invalid. It may have already been used or revoked. If you
            believe this is a mistake, please contact the author who invited you.
          </AlertDescription>
        </Alert>
      );
    }
    if (state.kind === 'expired') {
      return (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This invitation has expired. Please ask the author to send a new one if you'd still
            like to review.
          </AlertDescription>
        </Alert>
      );
    }
    if (state.kind === 'resolved') {
      const label =
        state.status === 'accepted'
          ? 'You have already accepted this invitation.'
          : state.status === 'declined'
            ? 'This invitation was declined. Thank you for letting us know.'
            : `This invitation is no longer active (${state.status}).`;
      return (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>{label}</AlertDescription>
        </Alert>
      );
    }
    // pending
    const mismatch =
      user?.email && state.inviteeEmail && user.email.toLowerCase() !== state.inviteeEmail.toLowerCase();

    // Visibility + intent drive both the heading and the footer disclosure.
    // Saying "reviews are public on PaperVerse" is a lie on a private paper:
    // the paper is only visible to the author + invitees, and human reviews
    // written during the private period stay private even after the author
    // later flips the paper to public (only AI reviews auto-release).
    const isPrivate = state.paperVisibility === 'private';
    const isShareOnly = isPrivate && state.intent === 'collaborate';

    const headingLead = isShareOnly
      ? `${state.authorName} has shared a confidential draft with you:`
      : isPrivate
        ? `${state.authorName} has invited you to review their confidential draft:`
        : `${state.authorName} has invited you to review:`;

    const acceptLabel = isShareOnly ? 'Accept and view draft' : 'Accept invitation';

    const footerCopy = isShareOnly ? (
      <>
        By accepting you confirm you have no undisclosed conflict of interest with this work. This
        is a confidential draft — please don't share its contents outside PaperVerse. Nothing you
        write here is public. You can opt out of future invitations from any address{' '}
        <Link to="/unsubscribe/review-invitations" className="underline">here</Link>.
      </>
    ) : isPrivate ? (
      <>
        By accepting you confirm you have no undisclosed conflict of interest with this work. This
        is a confidential draft: your review will be visible only to the author and you, and stays
        private even if the author later publishes the paper (unless they choose to release
        private-period reviews). Attributed to your account. You can opt out of future invitations
        from any address{' '}
        <Link to="/unsubscribe/review-invitations" className="underline">here</Link>.
      </>
    ) : (
      <>
        By accepting you confirm you have no undisclosed conflict of interest with this work.
        Reviews are public on PaperVerse and attributed to your account. You can opt out of future
        invitations from any address{' '}
        <Link to="/unsubscribe/review-invitations" className="underline">here</Link>.
      </>
    );

    return (
      <div className="space-y-5">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">{headingLead}</p>
          <h2 className="text-xl font-semibold leading-snug">{state.paperTitle}</h2>
          {state.expiresAt && (
            <p className="text-xs text-muted-foreground">
              Expires {new Date(state.expiresAt).toLocaleDateString()}
            </p>
          )}
        </div>

        {mismatch && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-sm">
              You're signed in as <strong>{user?.email}</strong>, but the invitation was sent to{' '}
              <strong>{state.inviteeEmail}</strong>.{' '}
              {isShareOnly
                ? 'Access will be granted to your current account.'
                : 'The review will be attributed to your current account.'}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          {!user ? (
            <Button onClick={handleAccept} className="gap-2">
              <LogIn className="h-4 w-4" />
              Sign in and accept
            </Button>
          ) : (
            <Button onClick={handleAccept} disabled={acting !== null}>
              {acting === 'accept' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {acceptLabel}
            </Button>
          )}
          <Button variant="outline" onClick={handleDecline} disabled={acting !== null}>
            {acting === 'decline' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Decline
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">{footerCopy}</p>
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead
        title="Review invitation — PaperVerse"
        description="Accept or decline a peer review invitation."
        noindex
      />
      <Navbar />
      <main className="flex-1 container max-w-2xl mx-auto px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle>Review invitation</CardTitle>
            <CardDescription>From a PaperVerse author</CardDescription>
          </CardHeader>
          <CardContent>{renderBody()}</CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default InvitationLanding;
